import { FC, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEntityBasePath } from "../../../../../../utils/entityPaths";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Autocomplete
} from "@mui/material";
import { CommentDto, Broker } from "../../../../../../api/Api";
import {
  useBrokerAssignees,
  useBrokerProgressOverride,
  useUpdateBroker
} from "../../../../dataHooks/brokerQueries";
import { createLifecycleActions } from "../../../../../../components/lifecycle/createLifecycleActions";
import { CommentsSection } from "../../../../../../components/lifecycle/commentsSection";
import { LifecycleDialog } from "../../../../../../components/lifecycle/lifecycleDialog";
import { SignOffNoteField } from "../../../../../../components/lifecycle/signOffNoteField";
import { useLifecycleComments } from "../../../../../../components/lifecycle/useLifecycleComments";
import { brokerAttachmentApi } from "../../../brokerAttachmentApi";
import { useRoles } from "../../../../../../auth/useRoles";
import { ownsLifecycle } from "../../../../../../auth/permissions";

type Props = {
  specificBroker: Broker;
};

type FormData = {
  mandateReview: string;
  mandateReviewAssignee: string;
  comments: CommentDto[];
  signOffNote: string;
};

/** The mandate review. The credit check moved to its own Credit Risk lifecycle. */
export const Risk: FC<Props> = ({ specificBroker }) => {
  const params = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const basePath = useEntityBasePath("broker");
  const roles = useRoles();
  const progressOverride = useBrokerProgressOverride(specificBroker, "risk");

  const [formData, setFormData] = useState<FormData>({
    mandateReview: specificBroker?.riskLifecycle?.mandateReview || "",
    mandateReviewAssignee:
      specificBroker?.riskLifecycle?.mandateReviewAssignee || "",
    comments: specificBroker?.riskLifecycle?.comments || [],
    signOffNote: specificBroker?.riskLifecycle?.signOffNote || ""
  });

  const handleClose = () => {
    navigate(`${basePath}/${params.id}`);
  };

  const riskAssignees = useBrokerAssignees("risk");

  const updateBroker = useUpdateBroker({ onSuccess: handleClose });
  const commentUpdate = useUpdateBroker();

  const buildBroker = (values: FormData & { complete?: boolean }): Broker => ({
    ...specificBroker,
    riskLifecycle: {
      ...specificBroker.riskLifecycle,
      ...values
    }
  });

  const {
    currentComment,
    setCurrentComment,
    addComment,
    deleteComment,
    isSaving
  } = useLifecycleComments({
    formData,
    setFormData,
    update: commentUpdate,
    buildEntityWithComments: (comments) =>
      buildBroker({ ...formData, comments })
  });

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const { handleSave, handleComplete } = createLifecycleActions({
    formData,
    currentComment,
    currentProgress: specificBroker.progress?.risk,
    canSetInProgress: ownsLifecycle(roles, "risk"),
    buildEntity: (values, progress) => {
      const saved = buildBroker(values);
      return {
        ...saved,
        progress: { ...saved.progress, risk: progress }
      };
    },
    mutate: updateBroker.mutate
  });

  // endsWith, not includes: the Credit Risk route ends in "creditrisk".
  const open = location.pathname.endsWith("/risk");

  const canComplete = !!formData.mandateReview;

  return (
    <LifecycleDialog
      open={open}
      onClose={handleClose}
      title="Risk"
      progressOverride={progressOverride}
      maxWidth="md"
      attachmentApi={brokerAttachmentApi}
      actions={{
        onClose: handleClose,
        onSave: handleSave,
        onComplete: handleComplete,
        canComplete,
        isSaving: updateBroker.isPending
      }}
    >
      <Box sx={{ paddingTop: 2 }} gap={2} display="flex" flexDirection="column">
        <Box display="flex" gap={2}>
          <FormControl sx={{ flex: 1 }} size="medium" required>
            <InputLabel>Mandate review</InputLabel>
            <Select
              label="Mandate review"
              value={formData.mandateReview}
              onChange={(e) => handleChange("mandateReview", e.target.value)}
            >
              <MenuItem value="included_in_mandate">
                <Typography variant="body1" component="span">
                  Included in mandate
                </Typography>
              </MenuItem>
              <MenuItem value="amendment_needed">
                <Typography variant="body1" component="span">
                  Amendment needed
                </Typography>
              </MenuItem>
            </Select>
          </FormControl>

          <Autocomplete
            sx={{ flex: 1 }}
            options={riskAssignees.data || []}
            getOptionLabel={(option) => `${option.name} (${option.email})`}
            value={
              riskAssignees.data?.find(
                (a) => a.email === formData.mandateReviewAssignee
              ) || null
            }
            onChange={(_, newValue) =>
              handleChange("mandateReviewAssignee", newValue?.email || "")
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Mandate review assignee"
                size="medium"
              />
            )}
          />
        </Box>

        <SignOffNoteField
          value={formData.signOffNote}
          onChange={(value) =>
            setFormData((prev) => ({ ...prev, signOffNote: value }))
          }
        />

        <CommentsSection
          loading={isSaving}
          comments={formData.comments}
          currentComment={currentComment}
          onCurrentCommentChange={setCurrentComment}
          saveComment={addComment}
          onDeleteComment={deleteComment}
        />
      </Box>
    </LifecycleDialog>
  );
};
